import { EVENT_TYPE } from '@eventbrite/discover-utils';
import { FormattedEvent } from '@eventbrite/event-renderer';
import {
    addFollow,
    FollowList,
    withFollowList,
} from '@eventbrite/follow-organizer-ebui';
import { gettext } from '@eventbrite/i18n';
import { useQueryClient } from '@tanstack/react-query';
import isEmpty from 'lodash/isEmpty';
import React, { useState } from 'react';
import { HEAP_PAGE_AREA } from '../../constants';
import './organizerFollowBucket.scss';

const listStyle = 'grid';
const size = 'mini';

const useOrganizers = ({
    placeId,
    isOnline,
}: {
    placeId: string;
    isOnline: boolean;
}) => {
    const queryClient = useQueryClient();
    const { pages } = queryClient.getQueryData<any>([
        'browse-buckets',
        placeId,
        isOnline,
    ]);
    const organizers = React.useMemo(() => {
        return pages
            .reduce((memo: string, page: { buckets: any }) => {
                const currentPageOrganizers = page.buckets.reduce(
                    (memo: string, bucket: any) => {
                        if (bucket && bucket.type === EVENT_TYPE) {
                            return [
                                ...memo,
                                ...bucket.content.map(
                                    (event: FormattedEvent) =>
                                        event.organizerId,
                                ),
                            ];
                        }

                        return memo;
                    },
                    [],
                );

                return [...memo, ...currentPageOrganizers];
            }, [])
            .slice(0, 20);
    }, [pages]);

    return organizers;
};

interface OrganizerFollowBucketProps {
    userId?: string;
    isAuthenticated?: boolean;
    isGDPRCountry?: boolean;
    placeId: string;
    isOnline: boolean;
    locale: string;
    usesLanguageSubdirectory: boolean;
}

export const OrganizerFollowBucket = (props: OrganizerFollowBucketProps) => {
    const [isListEmpty, setListEmpty] = useState(false);
    const { userId, isAuthenticated, isGDPRCountry, placeId, isOnline } = props;
    const organizers = useOrganizers({ placeId, isOnline });
    const hasNoOrganizers = !organizers || isEmpty(organizers);
    const shouldShowEmptyBucket = isListEmpty || hasNoOrganizers;
    const EnhancedFollowList = withFollowList(FollowList, props.locale);

    if (shouldShowEmptyBucket) {
        return <div className="organizer-follow-bucket__follow-empty" />;
    }

    const EnhancedFollowListWithData = addFollow(EnhancedFollowList);

    const localeSubdirectory =
        props.usesLanguageSubdirectory && props.locale
            ? `/${props.locale}`
            : '';

    return (
        <div
            className="
                    content-bucket
                    organizer-follow-bucket
                "
        >
            <div className="eds-l-pad-hor-3">
                <div
                    className="
                            bucket-header__title
                            eds-g-cell
                            eds-g-cell-9-12
                            "
                >
                    <h2
                        className="
                            eds-text-hs
                            eds-text-color--grey-700
                        "
                    >
                        {gettext('Featured Organizers')}
                    </h2>
                    <p className="eds-text-bm eds-text-color--grey-600">
                        {gettext(
                            'Follow the organizers from these events and get notified when they create new ones.',
                        )}
                    </p>
                </div>
            </div>
            <div className="organizer-follow-bucket__follow-container">
                <EnhancedFollowListWithData
                    isGDPRCountry={isGDPRCountry}
                    style={listStyle}
                    size={size}
                    onEmpty={() => setListEmpty(true)}
                    isClosable={true}
                    isHiddenWhenFollowed={true}
                    shouldHideDefaultDataPoint={true}
                    organizers={organizers}
                    userId={userId}
                    isAuthenticated={isAuthenticated}
                    pageArea={HEAP_PAGE_AREA}
                    locale={localeSubdirectory}
                />
            </div>
        </div>
    );
};

export default OrganizerFollowBucket;
